/**
 * Reject Request Dialog — W3-A · B7.
 *
 * Rejects a workflow instance with a mandatory reason (min 10 chars).
 * Backend contract:
 *   POST /api/workflows/instances/{id}/reject
 *     Idempotency-Key: <client-generated>
 *     body: { reason: string }  (min 10, max 500)
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { XCircle } from "@phosphor-icons/react";

import { api, formatApiError } from "../../../lib/api";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader,
    DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";


const MIN_REASON = 10;
const MAX_REASON = 500;


function newIdemKey(instanceId) {
    return `rj-${instanceId}-${Date.now()}-${Math.random()
        .toString(36).slice(2, 8)}`;
}


export default function RejectDialog({ open, instance, onClose, onRejected }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [key, setKey] = useState("");

    useEffect(() => {
        if (open && instance) {
            setReason("");
            setSaving(false);
            setKey(newIdemKey(instance.workflow_instance_id));
        }
    }, [open, instance]);

    if (!instance) return null;

    const projectName = instance.payload?.name || "this Project Request";
    const trimmed = reason.trim();
    const tooShort = trimmed.length < MIN_REASON;
    const tooLong = trimmed.length > MAX_REASON;

    async function submit() {
        if (tooShort || tooLong) return;
        setSaving(true);
        try {
            const r = await api.post(
                `/workflows/instances/${instance.workflow_instance_id}/reject`,
                { reason: trimmed },
                { headers: { "Idempotency-Key": key } },
            );
            toast.success("Request rejected. The initiator has been notified.");
            onRejected?.(r.data);
        } catch (err) {
            toast.error(formatApiError(err) || "Rejection failed.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="max-w-lg"
                data-testid="reject-request-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-800">
                        <XCircle
                            className="w-6 h-6 text-red-600"
                            weight="fill"
                        />
                        Reject Request
                    </DialogTitle>
                    <DialogDescription>
                        You are about to reject the Project Request
                        <strong> &ldquo;{projectName}&rdquo;</strong>. The
                        initiator will see the reason below on their
                        My Project Requests screen.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div>
                        <Label htmlFor="reject-reason">
                            Reason
                            {tooShort ? (
                                <span className="text-xs text-red-600 ml-2">
                                    ({MIN_REASON - trimmed.length} more chars required)
                                </span>
                            ) : (
                                <span className="text-xs text-emerald-700 ml-2">
                                    ({trimmed.length}/{MAX_REASON} chars)
                                </span>
                            )}
                        </Label>
                        <Textarea
                            id="reject-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={4}
                            placeholder="Explain why this request is being rejected …"
                            data-testid="reject-reason-input"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="reject-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={submit}
                        disabled={saving || tooShort || tooLong}
                        data-testid="reject-confirm-btn"
                    >
                        {saving ? "Rejecting…" : "Reject Request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
